
import React, { useState, useRef, useEffect, useCallback } from "react";
import useAxios from "../../hooks/useAxios";
import CloseIcon from "../../assets/images/x.svg";
import InfoIcon from "../../assets/images/info.svg";
import CopyIcon from "../../assets/images/copy.svg";
import SendIcon from "../../assets/images/send.svg";
import EmojiIcon from "../../assets/images/chatModalEmoji.svg";
import CircularLoader from "./CircularLoader";

const ChatModal = ({ isOpen, handleClose, chatData }) => {
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [localMessages, setLocalMessages] = useState([]);
  const [groupedMessages, setGroupedMessages] = useState({});
  const inputRef = useRef(null);
  const { axiosInstance } = useAxios();

  // Initialize local messages with chatData messages when component mounts or chatData changes
  useEffect(() => {
    if (chatData?.messages) {
      setLocalMessages(chatData.messages);
    }
  }, [chatData?.messages]);

  // Format date for display (Today, Yesterday, or day of week for recent dates)
  const formatDateHeader = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date();
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    
    // Reset hours to compare just the dates
    today.setHours(0, 0, 0, 0);
    yesterday.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    
    if (date.getTime() === today.getTime()) {
      return "Today";
    } else if (date.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else if (today.getTime() - date.getTime() < 7 * 24 * 60 * 60 * 1000) {
      // Within the last week, show day name
      return date.toLocaleDateString('en-US', { weekday: 'long' });
    } else {
      // Older than a week, show full date
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    }
  };

  // Group messages by date for date breaks
  useEffect(() => {
    if (localMessages.length > 0) {
      const grouped = localMessages.reduce((groups, message) => {
        if (!message.dateSent) return groups;
        
        const date = new Date(message.dateSent);
        const today = new Date();
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        
        // Reset hours to compare just the dates
        const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const yesterdayDate = new Date(yesterday.getFullYear(), yesterday.getMonth(), yesterday.getDate());
        const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        
        // Create a key based on the date category, not the exact date
        let dateKey;
        if (messageDate.getTime() === todayDate.getTime()) {
          dateKey = "today";
        } else if (messageDate.getTime() === yesterdayDate.getTime()) {
          dateKey = "yesterday";
        } else if (todayDate.getTime() - messageDate.getTime() < 7 * 24 * 60 * 60 * 1000) {
          // Within the last week, use day name
          dateKey = date.toLocaleDateString('en-US', { weekday: 'long' }).toLowerCase();
        } else {
          // Older than a week, use full date
          dateKey = date.toISOString().split('T')[0]; // YYYY-MM-DD format
        }
        
        if (!groups[dateKey]) {
          groups[dateKey] = {
            displayDate: formatDateHeader(date),
            messages: []
          };
        }
        
        groups[dateKey].messages.push(message);
        return groups;
      }, {});
      
      // Sort messages within each group by date
      Object.keys(grouped).forEach(key => {
        grouped[key].messages.sort((a, b) => {
          return new Date(a.dateSent) - new Date(b.dateSent);
        });
      });
      
      setGroupedMessages(grouped);
    }
  }, [localMessages]);

  // Scroll to bottom only in specific conditions
  const messagesEndRef = useRef(null);
  const scrollAreaRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [initialScrollDone, setInitialScrollDone] = useState(false);
  const [prevMessagesLength, setPrevMessagesLength] = useState(0);
  
  // Handle scroll events to determine if we should auto-scroll
  const handleScroll = () => {
    if (!scrollAreaRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = scrollAreaRef.current;
    // If user has scrolled up more than 100px from bottom, disable auto-scroll
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShouldAutoScroll(isNearBottom);
  };
  
  // Manual scroll to bottom function
  const scrollToBottom = useCallback(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, []);
  
  // Initial scroll to bottom when chat is first opened
  useEffect(() => {
    if (isOpen && !initialScrollDone) {
      // Use a timeout to ensure the DOM is fully rendered
      setTimeout(() => {
        scrollToBottom();
        setInitialScrollDone(true);
      }, 100);
    }
    
    // Reset state when modal is closed
    if (!isOpen) {
      setInitialScrollDone(false);
      setShouldAutoScroll(true);
    }
  }, [isOpen, initialScrollDone, scrollToBottom]);
  
  // Check if new messages have arrived
  useEffect(() => {
    const currentMessagesLength = localMessages.length;
    
    // If new messages have arrived and we should auto-scroll
    if (currentMessagesLength > prevMessagesLength && shouldAutoScroll) {
      // Use a timeout to ensure the DOM is updated with new messages
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
    
    setPrevMessagesLength(currentMessagesLength);
  }, [localMessages, prevMessagesLength, shouldAutoScroll, scrollToBottom]);

  // Function to fetch messages
  const getMessagesData = useCallback(async () => {
    if (!chatData?.student?.Student_ID) return;
    
    try {
      const { data } = await axiosInstance.post("/tutor/messages", { 
        studentId: chatData.student.Student_ID 
      });
      
      if (data?.messages) {
        setLocalMessages(data.messages);
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  }, [axiosInstance, chatData?.student?.Student_ID]);

  // Poll for new messages every 5 seconds
  useEffect(() => {
    if (!chatData?.student?.Student_ID) return;
    
    // Initial fetch
    getMessagesData();
    
    // Set up polling interval
    const intervalId = setInterval(() => {
      getMessagesData();
    }, 5000); // Poll every 5 seconds
    
    // Clean up interval on unmount
    return () => clearInterval(intervalId);
  }, [chatData?.student?.Student_ID, getMessagesData]);

  // Common emojis
  const emojis = ["😊", "😂", "👍", "❤️", "🎉", "👋", "🙏", "👏", "🔥", "✅", "⭐", "🤔", "😍", "👌", "🙌", "🤝", "🎊", "🌟", "💯", "🤩"];

  const tutoringCode = "https://student-portal.stepuptutoring.org/student/" + chatData?.student?.Tutoring_Code;

  const sendMessage = async () => {
    if (!message.trim() || isSending) return;
    
    setIsSending(true);
    try {
      await axiosInstance.post('tutor/send-message', {
        studentId: chatData?.student?.Student_ID,
        message: message.trim()
      });
      
      // Add the new message to local messages
      const newMessage = {
        id: `local-${Date.now()}`, // Temporary ID for local rendering
        body: message.trim(),
        isFromTutor: "Yes", // Assuming messages sent from this UI are from the tutor
        dateSent: new Date().toISOString(),
      };
      
      setLocalMessages(prevMessages => [...prevMessages, newMessage]);
      setMessage("");
    } catch (error) {
      console.error("Failed to send message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tutoringCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  if (!isOpen) return null;


  return (
    <>
      <div
        className="fixed inset-0 z-50 overflow-auto bg-black bg-opacity-50 flex items-center justify-center p-4"
        onClick={handleClose}>
        <div
          className="bg-white rounded-3xl shadow-lg w-full max-w-[950px] transform transition-all duration-300 relative z-60"
          onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col h-[80vh]">
            {/* Sticky header */}
            <div className="sticky top-0 z-20 bg-white p-10 pb-4 border-b border-gray-200 rounded-tl-3xl rounded-tr-3xl">
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <div>
                      <h1 className="text-[24px] font-semibold text-[#000] leading-normal font-roboto">
                        {chatData?.student?.Guardian_First_Name ?? ""}{" "}{chatData?.student?.Guardian_Last_Name ?? ""}
                      </h1>
                      {(chatData?.student?.First_Name || chatData?.student?.Last_Name) && (
                        <p className="text-[13px] font-bold text-[#5B5B5B] leading-normal font-roboto">
                          Guardian of {chatData?.student?.First_Name ?? ""} {chatData?.student?.Last_Name ?? ""}
                        </p>
                        )}
                  </div>
                 <div>
                    <div className="group relative">
                      <p className="text-[#000] text-sm flex items-center">
                        Guardian's Number: {chatData?.student?.studentTwilio}
                        <span className="ml-1 cursor-help text-gray-500">ⓘ</span>
                      </p>
                      <div className="absolute z-30 hidden group-hover:block bg-gray-800 text-white text-xs rounded p-2 w-64 top-full right-0 mt-1">
                        This isn't the guardian's real number — it's a private forwarding number that keeps their info safe while still letting you text them.
                      </div>
                    </div>
                    <p className="text-[#0D9488] text-sm flex items-center gap-1 cursor-pointer" onClick={handleCopy}>{copied ? "Copied!" : "Copy Meeting Invitation"}
                    <img src={CopyIcon} alt="Copy Icon" /></p>
                 </div>
                </div>
              </div>
              
              <button
                className="absolute right-[10px] top-[10px] w-10 h-10 rounded-full flex items-center justify-center"
                onClick={handleClose}>
                <img src={CloseIcon} alt="Close Icon" />
              </button>

              {chatData?.student?.Language === "Spanish" && (
                <div className="bg-[#F8FAFC] rounded-2xl p-4 min-h-[52px] flex gap-2 border border-[#E5E7EB]">
                  <div className="flex items-center gap-5">
                    <img src={InfoIcon} alt="info Icon" />
                    <span className="text-[13px] font-normal leading-5 text-gray-800">
                      Contact is a Spanish speaker. Incoming and outgoing messages will be automatically translated by our
                      system.
                    </span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Scrollable message area */}
            <div 
              className="flex-1 overflow-y-auto px-10"
              ref={scrollAreaRef}
              onScroll={handleScroll}
            >

              {Object.keys(groupedMessages).length > 0 ? (
                Object.entries(groupedMessages).map(([dateKey, { displayDate, messages }]) => (
                  <div key={dateKey} className="mb-8">
                    <div className="sticky top-0 z-10 bg-white py-2 mb-4 border-b border-gray-200">
                    <h3 className="text-center text-sm font-medium text-gray-500">{displayDate}</h3>
                  </div>
                  
                  {messages.map((message) => {
                    const formattedTime = message.dateSent
                      ? new Date(message.dateSent).toLocaleString("en-US", {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })
                      : "";

                    return (
                      <div
                        key={message.id}
                        className={`space-y-2 mb-5 ${message.isFromTutor === "No" ? "" : "flex flex-col items-end"}`}>
                        <div
                          className={`rounded-2xl p-4 max-w-[50%] text-[13px] ${
                            message.isFromTutor === "No"
                              ? "bg-white text-gray-800 border border-[#E5E7EB]"
                              : "bg-[#0C6980] text-white"
                          }`}>
                          <p className="font-normal leading-5" style={{ letterSpacing: "0.07px" }}>
                            {message.body}
                          </p>
                        </div>
                        {message.isFromTutor === "No" ? (
                          <div className="flex flex-col">
                            <span className="text-[#5B5B5B] font-roboto text-[13px] font-bold leading-normal">
                              {message.messageType === "activity"
                                ? `${chatData?.student?.Guardian_First_Name ?? ""} ${chatData?.student?.Guardian_Last_Name ?? ""}`
                                : "Step Up Admin"}
                            </span>
                            <span className="text-[#C5C5C5] font-roboto text-[13px] font-bold leading-normal">
                              {formattedTime}
                            </span>
                          </div>
                        ) : (
                          <span className="text-[#C5C5C5] font-roboto text-[13px] font-bold leading-normal">
                            {formattedTime}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  </div>
                ))
              ) : (
                <p className="text-center text-gray-500">No messages yet</p>
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>
          <div className="bg-[#F3F4F6] w-full flex px-10 py-4 rounded-bl-3xl rounded-br-3xl gap-5">
            <div className="relative w-full">
              <input 
                type="text" 
                className="w-full bg-white border border-[#574E4E] rounded-[10px] pr-10 pl-3 py-2"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    sendMessage();
                  }
                }}
                ref={inputRef}
              />
              <button 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                type="button"
              >
                <img src={EmojiIcon} alt="Emoji" className="w-5 h-5" />
              </button>
              {showEmojiPicker && (
                <div className="absolute bottom-12 right-0 z-10 bg-white border border-gray-300 rounded-lg shadow-lg p-2">
                  <div className="grid grid-cols-5 gap-2">
                    {emojis.map((emoji, index) => (
                      <button
                        key={index}
                        className="text-2xl hover:bg-gray-100 rounded p-1"
                        onClick={() => {
                          setMessage((prev) => prev + emoji);
                          setShowEmojiPicker(false);
                          inputRef.current?.focus();
                        }}
                        type="button"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <button 
              onClick={sendMessage}
              disabled={!message.trim() || isSending}
              className={`${!message.trim() || isSending ? 'opacity-50' : 'opacity-100'}`}
            >
              {isSending ? (
                <CircularLoader className="w-5 h-5" />
              ) : (
                <img src={SendIcon} alt="Send icon" />
              )}
            </button>
          </div>
        </div>
        
      </div>

    </>
  );
};

export default ChatModal;
